/**
 * 合同预览「主要内容」与「合同金额」的共用校验。
 * 主要内容必须是对交易标的物和交易模式的概述，不能摘抄条款；
 * 金额按交易结构分类表述，不能是条款编号，也不能误取分期支付额当作合同价款。
 */

/** 预览未能得到可用概述时的提示。 */
export const PREVIEW_MAIN_CONTENT_UNAVAILABLE = '暂未能生成合同概述，请重新打开预览以调用模型生成。';

/** 合同首页字段行，不应作为「主要内容」。 */
export const PREVIEW_MAIN_CONTENT_FIELD_LINE = /^(?:发包[人方]|承包[人方]|监理[人方单位]?|建设[单位方]|工程名称|工程地点|工程内容|建设规模|承包范围|合同编号|签订[地点时间]|甲方|乙方|丙方|丁方|住所|地址|法定代表人|统一社会信用|开户|账号|邮编|电话|传真|电子邮箱|联系人)\s*[：:]/u;

/** 整段文本就是条款编号（如 5.1.1、第5.1.1条）。 */
const CLAUSE_NUMBER_AS_AMOUNT = /^(?:第)?[0-9０-９]{1,2}(?:[.．][0-9０-９]{1,2}){1,3}(?:条)?$/u;

const MONEY_UNIT = /人民币|RMB|USD|美元|港币|欧元|万元|亿元|万圆|元整|[万亿]元|[元圆万亿]/u;
const CHINESE_NUMERAL = /[零〇一二三四五六七八九十百千万亿壹贰叁肆伍陆柒捌玖拾佰仟]/u;
/** 金额数字片段（阿拉伯或中文），须带元/万元等单位。 */
const MONEY_SPAN = /(?:人民币|RMB)?[ \t　]*(?:[0-9０-９,，]{1,18}(?:[.．][0-9０-９]{1,2})?[ \t　]*(?:万元|亿元|元|万)|[零〇一二三四五六七八九十百千万亿壹贰叁肆伍陆柒捌玖拾佰仟]+[万亿元圆整]+)/u;
/** 已知分类前缀：有前缀的表述优先保留整句。 */
const AMOUNT_KIND_PREFIX = /^(?:合同总价|固定总价|暂定金额|暂定总价|固定收益|固定价款|固定租金|固定\+分成|固定加分成|单价|费率|服务费|报酬)[：:]/u;
/** 分期支付语境：不得当作合同价款。 */
const INSTALLMENT_CONTEXT = /当季|每季|季度|分期|每期|每(?:个)?月|按月|月度/u;

/**
 * 预览合同金额的交易结构类别。
 * - fixed-total：单一固定总价
 * - provisional：暂定总价/暂定价款
 * - fixed-periodic：按年/月等周期支付的固定收益或租金
 * - fixed-plus-share：固定收益（或固定价款）加分成
 * - unit-price：单价结算，无固定总价
 * - fee-rate：费率/比例计取
 */
export type PreviewContractAmountKind =
  | 'fixed-total'
  | 'provisional'
  | 'fixed-periodic'
  | 'fixed-plus-share'
  | 'unit-price'
  | 'fee-rate';

const KIND_LABEL: Record<PreviewContractAmountKind, string> = {
  'fixed-total': '合同总价',
  provisional: '暂定金额',
  'fixed-periodic': '固定收益',
  'fixed-plus-share': '固定+分成',
  'unit-price': '单价',
  'fee-rate': '费率',
};

/**
 * 判断预览「合同主要内容」是否不合格：首页字段表、条款摘抄或过短。
 * @param content 模型或本地生成的摘要
 */
export function isPoorPreviewMainContent(content: string): boolean {
  const trimmed = String(content ?? '').trim();
  if (!trimmed || trimmed.length < 24) return true;
  if (trimmed === PREVIEW_MAIN_CONTENT_UNAVAILABLE) return true;
  const fieldHits = (trimmed.match(/(?:发包[人方]|承包[人方]|工程名称|工程地点|承包范围|建设规模|甲方|乙方|丙方)\s*[：:]/gu) ?? []).length;
  if (fieldHits >= 2) return true;
  if (fieldHits >= 1 && /(?:公司|集团|有限公司).{0,40}(?:公司|集团|有限公司)/u.test(trimmed)) return true;
  if (/^(?:第[一二三四五六七八九十百零〇0-9]+[条章节]|[0-9]{1,2}[.．][0-9])/u.test(trimmed)) return true;
  const dottedClauseHits = (trimmed.match(/(?:^|[。；\s　])[0-9]{1,2}[.．][0-9]{1,2}(?:[.．][0-9]{1,2})?/gu) ?? []).length;
  if (dottedClauseHits >= 2) return true;
  if (/根据《/.test(trimmed) && /为明确责任|结合本(?:工程|合同|协议)/u.test(trimmed)) return true;
  const articleHits = (trimmed.match(/第[一二三四五六七八九十百零〇0-9]+条/gu) ?? []).length;
  if (articleHits >= 2) return true;
  return false;
}

/**
 * 已落盘的预览是否仍是条款摘抄，打开时应重新调用模型生成概述。
 * @param content 预览 JSON 中的 mainContent
 */
export function shouldRefreshPreviewMainContent(content: string, contractType = ''): boolean {
  const trimmed = String(content ?? '').trim();
  // 这个占位文本代表上一次模型调用没有得到摘要。打开旧预览时必须重试，
  // 否则即使后台已经恢复，历史 JSON 也会永久显示“暂未能生成”。
  if (!trimmed || trimmed === PREVIEW_MAIN_CONTENT_UNAVAILABLE) return true;
  if (/服务|承揽|技术/u.test(String(contractType ?? ''))
    && /发包人与承包人之间的施工|施工范围.{0,24}工程价款|施工进度.{0,20}竣工验收|质量保修和违约责任等履行安排/u.test(trimmed)) return true;
  return isPoorPreviewMainContent(trimmed);
}

/** 已落盘的类型理由若仍是主体清单或施工模板，也应重新调用模型。 */
export function shouldRefreshPreviewTypeAnalysis(analysis: string, contractType = ''): boolean {
  const text = String(analysis ?? '').trim();
  if (!text) return true;
  if (/(?:依据|根据)\s*[：:]?\s*(?:甲方|乙方|丙方|丁方)\s*[：:]/u.test(text)) return true;
  if (/服务|承揽|技术/u.test(String(contractType ?? ''))
    && /发包[—\-]承包|施工范围|工程价款结算|施工进度|质量保修/u.test(text)
    && !/检测|监测|测绘|技术报告|服务成果|服务报酬/u.test(text)) return true;
  return false;
}

/**
 * 已落盘的预览金额是否需要重新识别。
 * 条款编号、空值清洗失败、分期支付额、以及未按交易结构分类的裸金额均应刷新。
 * @param value 预览 JSON 中的 contractAmount
 */
export function shouldRefreshPreviewContractAmount(value: string | undefined): boolean {
  const raw = String(value ?? '').trim();
  if (!raw) return false;
  if (!sanitizePreviewContractAmount(raw)) return true;
  if (INSTALLMENT_CONTEXT.test(raw)) return true;
  // 历史预览常只存「300万元」这类裸金额，缺少交易结构标签，打开时按新规则重识。
  if (!AMOUNT_KIND_PREFIX.test(raw) && !/总价|暂定|固定|分成|单价|费率|\/年|每年|\/月/u.test(raw)) return true;
  return false;
}

/**
 * 从一段文字中抽出像价款的片段。
 * @param value 候选金额表述
 */
function extractMoneySpan(value: string): string {
  const match = value.match(MONEY_SPAN);
  return match?.[0] ? match[0].replace(/[ \t　]+/gu, '') : '';
}

/**
 * 规范化金额数字片段：去空白，保留币种与单位。
 * @param value 原始金额片段
 */
function normalizeMoneyFragment(value: string): string {
  return sanitizePreviewContractAmount(String(value ?? '').replace(/[ \t　]+/gu, '').trim()) || extractMoneySpan(value);
}

/**
 * 组装带交易结构标签的预览金额。
 * @param kind 金额类别
 * @param body 标签后的正文（已含数额与必要说明）
 */
export function formatPreviewContractAmount(kind: PreviewContractAmountKind, body: string): string {
  const cleaned = String(body ?? '').replace(/\s+/gu, ' ').trim();
  if (!cleaned) return '';
  const labeled = `${KIND_LABEL[kind]}：${cleaned}`;
  return sanitizePreviewContractAmount(labeled).slice(0, 200);
}

/**
 * 清洗预览合同金额：丢掉条款编号；保留分类标签与合理结构表述。
 * @param value 模型或规则抽出的金额
 */
export function sanitizePreviewContractAmount(value: string | undefined): string {
  const amount = String(value ?? '').trim();
  if (!amount) return '';
  if (/^元$/u.test(amount) || /^[¥￥]\s*元$/u.test(amount)) return '';
  if (INSTALLMENT_CONTEXT.test(amount) && !/\/年|每年|总价|暂定|分成/u.test(amount)) return '';

  const prefixMatch = amount.match(AMOUNT_KIND_PREFIX);
  const body = prefixMatch ? amount.slice(prefixMatch[0].length).trim() : amount;
  const compact = body.replace(/[\s　]/gu, '');
  if (!compact) return '';
  if (CLAUSE_NUMBER_AS_AMOUNT.test(compact)) return '';
  if (/^[0-9０-９,，.．]+$/u.test(compact)) {
    const digitsOnly = compact.replace(/[,，.．]/gu, '');
    if (/[.．]/u.test(compact) || digitsOnly.length < 4) return '';
    // 无小数点的长数字串仍可作金额；有分类前缀时带前缀返回。
    return (prefixMatch ? `${prefixMatch[0]}${compact}` : compact).slice(0, 200);
  }

  const money = extractMoneySpan(body);
  if (money && !CLAUSE_NUMBER_AS_AMOUNT.test(money)) {
    if (prefixMatch) {
      // 分类表述可保留分成比例、周期等非纯金额文字，但整句不宜过长。
      const structured = body.length <= 120 && (MONEY_UNIT.test(body) || CHINESE_NUMERAL.test(body) || /分成|比例|NOI|单价|费率|%/u.test(body))
        ? body.replace(/\s+/gu, ' ').trim()
        : money;
      return `${prefixMatch[0]}${structured}`.slice(0, 200);
    }
    // 无分类前缀时，仅接受较短的纯金额片段，供旧数据兼容；新抽取应走 formatPreviewContractAmount。
    return (body.length <= 40 && (MONEY_UNIT.test(body) || CHINESE_NUMERAL.test(body)) ? body.replace(/\s+/gu, '') : money).slice(0, 200);
  }
  if (prefixMatch && MONEY_UNIT.test(body) && CHINESE_NUMERAL.test(body)) return amount.replace(/\s+/gu, ' ').trim().slice(0, 200);
  if (MONEY_UNIT.test(body) && CHINESE_NUMERAL.test(body)) return amount.slice(0, 200);
  return '';
}

/**
 * 在窗口文本中找金额，并跳过分期支付语境。
 * @param window 局部正文
 */
function moneyInWindow(window: string): string {
  if (INSTALLMENT_CONTEXT.test(window) && !/每年|\/年|年固定|年租金|年服务费/u.test(window)) return '';
  const match = window.match(MONEY_SPAN);
  return match ? normalizeMoneyFragment(match[0]) : '';
}

/**
 * 从正文按交易结构分类抽取合同金额；找不到明确结构时留空，不再抓取正文第一个「人民币」。
 * @param text 合同正文
 */
export function inferPreviewContractAmount(text: string): string {
  const source = String(text ?? '').slice(0, 40_000);

  // 1) 暂定总价 / 暂定价款
  const provisional = source.match(/(?:暂定(?:价(?:款|格)?|金额|总价)|暂定价款)[ \t　]*[：:为]?[ \t　]*([^\n；;]{2,80})/u);
  if (provisional?.[1]) {
    const money = moneyInWindow(provisional[1]) || normalizeMoneyFragment(provisional[1]);
    if (money) return formatPreviewContractAmount('provisional', money);
  }

  // 2) 固定收益/租金 + 分成（合作经营、收益权类）
  const hasShare = /(?:分成|利润分配|收益分配|超额收益|浮动收益)/u.test(source) || /\bNOI\b/u.test(source);
  /**
   * 抽取年固定收益数额。必须出现「每年」或明确的年收益标签；
   * 命中片段若落在季度/月度分期语境中则丢弃。
   */
  const pickAnnualFixed = (): string => {
    const patterns = [
      /(?:固定收益|固定租金|固定价款)[^\n]{0,80}每年[^\n]{0,40}支付(?:\s*(?:人民币|RMB))?\s*([0-9０-９,，]+(?:[.．][0-9０-９]+)?)\s*(万元|亿元|元|万)/u,
      /每年[^\n]{0,40}支付(?:\s*(?:人民币|RMB))?\s*([0-9０-９,，]+(?:[.．][0-9０-９]+)?)\s*(万元|亿元|元|万)/u,
      /年(?:固定)?(?:收益|租金|价款)[^\n]{0,40}((?:人民币|RMB)\s*[0-9０-９,，]+(?:[.．][0-9０-９]+)?\s*(?:万元|亿元|元|万))/u,
    ];
    for (const pattern of patterns) {
      const match = source.match(pattern);
      if (!match) continue;
      // 只看命中片段及紧邻前文，避免把下一句的「季度」误判到年固定收益上。
      const start = Math.max(0, (match.index ?? 0) - 16);
      const window = source.slice(start, (match.index ?? 0) + match[0].length);
      if (INSTALLMENT_CONTEXT.test(window)) continue;
      const money = normalizeMoneyFragment(match[2] ? `${match[1]}${match[2]}` : match[1]);
      if (money) return money;
    }
    return '';
  };
  const annualFixedMoney = pickAnnualFixed();
  if (hasShare && annualFixedMoney) {
    const share = source.match(/按\s*(NOI|净利润|经营收益|超额收益)\s*的\s*(\d{1,2}(?:\.\d+)?)\s*%/u)
      || source.match(/按\s*(\d{1,2}(?:\.\d+)?)\s*%\s*(?:比例)?\s*分成/u);
    const sharePart = share?.[2]
      ? `另按${share[1]}的${share[2]}%分成`
      : share?.[1] && /^\d/u.test(share[1])
        ? `另按${share[1]}%分成`
        : '另按约定比例分成';
    return formatPreviewContractAmount('fixed-plus-share', `${annualFixedMoney}/年，${sharePart}`);
  }

  // 3) 纯周期固定收益 / 租金（无分成）
  if (annualFixedMoney) {
    return formatPreviewContractAmount('fixed-periodic', `${annualFixedMoney}/年`);
  }
  const monthlyFixed = source.match(/(?:月租金|每月租金|月服务费)[^\n]{0,20}(?:\s*(?:人民币|RMB))?\s*([0-9０-９,，]+(?:[.．][0-9０-９]+)?)\s*(万元|亿元|元|万)/u);
  if (monthlyFixed) {
    const fixed = normalizeMoneyFragment(`${monthlyFixed[1]}${monthlyFixed[2]}`);
    if (fixed) return formatPreviewContractAmount('fixed-periodic', `${fixed}/月`);
  }

  // 4) 明确标注的合同总价 / 固定总价
  const total = source.match(/(?:合同(?:总)?价(?:款)?|合同金额|总价款|固定总价|总价)[ \t　]*[：:为是]?[ \t　]*([^\n；;]{2,60})/u);
  if (total?.[1] && !INSTALLMENT_CONTEXT.test(total[1])) {
    const money = moneyInWindow(total[1]) || normalizeMoneyFragment(total[1]);
    if (money) return formatPreviewContractAmount('fixed-total', money);
  }

  // 5) 单价结算（无总价）
  const unit = source.match(/(?:单价|综合单价)[ \t　]*[：:为是]?[ \t　]*([^\n；;]{2,40})/u);
  if (unit?.[1]) {
    const money = moneyInWindow(unit[1]) || normalizeMoneyFragment(unit[1]);
    if (money) return formatPreviewContractAmount('unit-price', money);
  }

  // 6) 费率 / 比例计取且无固定总价
  const rate = source.match(/(?:费率|服务费率|管理费率)[ \t　]*[：:为是]?[ \t　]*(\d{1,2}(?:\.\d+)?\s*%)/u);
  if (rate?.[1] && !/(?:合同(?:总)?价(?:款)?|合同金额|总价款)/u.test(source)) {
    return formatPreviewContractAmount('fee-rate', rate[1].replace(/\s+/gu, ''));
  }

  // 不再使用「正文第一个人民币金额」兜底，避免把季度款、保证金等误当作合同价款。
  return '';
}
